// Domain types for the rules engine.
//
// Everything here is plain data and JSON-serialisable: no class instances, no
// Maps, no Sets, no functions. The engine mutates a GameState in place and
// records what it did in an event log, and an AI needs to clone whole states
// cheaply, so structuredClone has to be able to round-trip the whole thing.
//
// Rule numbers in comments refer to the Riftbound Core Rules (2026-07-16),
// extracted to .references/extracted/ruleset.txt.

// Type-only, and effects.ts imports only types from here, so the cycle is
// erased at compile time and never exists at runtime.
import type { Ability, Instruction, ObjectFilter } from './effects.ts';

export type PlayerId = 0 | 1;

/** Instance identity. Distinct from the card's printing id, which is a string. */
export type ObjectId = number;

export const DOMAINS = ['fury', 'calm', 'mind', 'body', 'chaos', 'order'] as const;
export type Domain = (typeof DOMAINS)[number];

export type CardType =
  | 'unit' | 'champion' | 'spell' | 'signatureSpell'
  | 'gear' | 'legend' | 'battlefield' | 'rune' | 'token';

/** Types that stay on the board once played (133.4.a). Runes are not permanents. */
export const PERMANENT_TYPES: readonly CardType[] = ['unit', 'champion', 'gear', 'token'];

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'showcase';

/** 133.7. Supertypes that apply across card types. */
export type SuperType = 'champion' | 'signature' | 'basic' | 'token';

// -- zones and locations -----------------------------------------------------

export type ZoneKind =
  | 'mainDeck' | 'runeDeck' | 'hand' | 'trash' | 'banishment'
  | 'championZone' | 'legendZone' | 'board' | 'chain' | 'facedown';

export type Zone =
  | { kind: 'mainDeck' | 'runeDeck' | 'hand' | 'trash' | 'banishment' | 'championZone' | 'legendZone'; player: PlayerId }
  | { kind: 'board' }
  | { kind: 'chain' }
  /** 107.3: one per battlefield, holds cards put there by Hide. */
  | { kind: 'facedown'; battlefield: number };

/** 197: where a permanent or rune sits on the board. */
export type Location =
  | { kind: 'base'; player: PlayerId }
  | { kind: 'battlefield'; index: number };

export const sameLocation = (a: Location | null, b: Location | null): boolean => {
  if (!a || !b) return a === b;
  if (a.kind !== b.kind) return false;
  return a.kind === 'base' ? a.player === (b as { player: PlayerId }).player
    : a.index === (b as { index: number }).index;
};

export const locationKey = (l: Location): string =>
  l.kind === 'base' ? `base:${l.player}` : `bf:${l.index}`;

// -- resources ---------------------------------------------------------------

/**
 * 165. A player's available Energy and Power. `any` is [A], universal Power that
 * can pay a cost of any domain.
 */
/**
 * What a payment is for. Energy added under a restriction ("Use only to play
 * spells") can only be spent when the purpose matches.
 */
export type SpendPurpose =
  | 'spell' | 'unit' | 'gear' | 'unitAbility' | 'gearAbility' | 'ability' | 'other';

/** The narrowing an [Add] can carry, as printed on the card. */
export type SpendOnly =
  | 'spells' | 'units' | 'gear' | 'showdowns'
  | 'unitsOrUnitAbilities' | 'gearOrGearAbilities';

/**
 * A continuous effect created during a turn and discarded at the end of it.
 * Board passives are read off permanents; these have no permanent to live on.
 */
/** Something that happened, waiting to be matched against triggered abilities. */
export interface TriggerEvent {
  /** Matches the `on` of a Trigger. */
  on: string;
  /** Whose action it was, for the "you ..." triggers. */
  player: PlayerId;
  /** The object it happened to, when there is one, for filter matching. */
  subject?: ObjectId;
  /** 144. Where a move started, for "when a unit moves from here". */
  from?: Location;
}

/** An effect waiting for a moment to arrive (363.3). */
export interface DelayedEffect {
  /** Whose beginning/main/ending the effect is watching. */
  player: PlayerId;
  /**
   * 363.3. The moment being waited for.
   *
   * `hold` is not a phase: "when they hold, return it to their hand" waits on
   * something a player does, and may never come. It is kept here rather than
   * as a triggered ability because the card says it happens "even if I'm no
   * longer on the board" -- an ability on an object cannot outlive it.
   */
  when: 'startOfPhase' | 'endOfTurn' | 'hold';
  phase?: Phase;
  /**
   * The turn it was created on. "Your next Main Phase" must not fire during
   * the Main Phase it was created in, so the turn is checked as well.
   */
  createdTurn: number;
  /** The object that scheduled it, for "me" and "here" to still resolve. */
  source: ObjectId;
  /**
   * What the scheduling effect had chosen, so "return **it**" still means the
   * card that was banished rather than nothing.
   */
  chosen?: ObjectId[];
  effects: unknown[];
}

export type TurnEffect =
  /** "Opponents can't play cards this turn." */
  | {
    k: 'cantPlayCards'; player: PlayerId;
    /** "can't play *spells* this turn" -- narrower than all cards. */
    only?: 'spell' | 'unit' | 'gear';
  }
  /** "The next spell you play this turn deals N Bonus Damage." */
  | { k: 'nextSpellBonusDamage'; player: PlayerId; amount: number }
  /** "The next unit you play this turn enters ready." */
  | { k: 'nextUnitEntersReady'; player: PlayerId }
  /** "Units you play this turn enter ready." Every one, not just the next. */
  | { k: 'unitsEnterReady'; player: PlayerId }
  /** 426. "Buffs give an additional +1 [M] to friendly units this turn." */
  | { k: 'buffBonus'; player: PlayerId; amount: number }
  /**
   * 356.4. "You may play this from your trash for [A]" -- an offer this card
   * makes about itself, on the way to the trash.
   *
   * A turn effect rather than a `flowCost`: Flow is printed on the card and
   * true every turn, while this is granted once, by a resolution that has
   * already happened, and is gone when the turn is.
   */
  | {
    k: 'playFromTrash'; player: PlayerId; object: ObjectId;
    price: { energy: number; power: number; domain?: Domain | 'any' };
  }
  /** 811. "You can hide cards ignoring costs this turn." */
  | { k: 'freeHide'; player: PlayerId }
  /** 356. "The next spell you play this turn costs [5] less." */
  | {
    k: 'nextSpellDiscount'; player: PlayerId; energy: number;
    /** "Your next card costs [2][A][A] less" -- Power comes off too. */
    power?: number;
    /** Spells unless the card says "card", which is anything at all. */
    anyCard?: boolean;
  }
  /**
   * 820. "Give the next spell you play this turn [Repeat] equal to its cost."
   * `fromOwnCost` is that form: the price is whatever the spell itself costs,
   * which is only known once a spell is chosen.
   */
  | {
    k: 'nextSpellRepeat'; player: PlayerId;
    energy: number; power: number; fromOwnCost?: boolean;
  }
  /**
   * 356. "Your non-token units cost [1] more to play this turn." The same
   * shape as the `costModifier` passive, but for a turn rather than while a
   * permanent stands there.
   */
  | { k: 'costThisTurn'; player: PlayerId; filter: ObjectFilter; energy: number }
  /** 705. "When any unit takes damage this turn, kill it." */
  | { k: 'anyDamageKills'; player: PlayerId }
  /**
   * 705. "Prevent the next 7 damage that would be dealt to it this turn."
   *
   * A pool that absorbs damage across however many instances it takes, unlike
   * `nextDamage`, which is spent whole by the first one.
   */
  | { k: 'damageShield'; player: PlayerId; object: ObjectId; amount: number }
  /**
   * 705. "Double all damage that would be dealt to it this turn." The mirror of
   * `damageShield`: a standing multiplier on one object rather than a pool that
   * absorbs, so it is not spent by the first hit.
   */
  | { k: 'damageMultiplier'; player: PlayerId; object: ObjectId; factor: number }
  /**
   * 144. "They can't move it this turn." A prohibition on one object for the
   * rest of the turn, unlike the `cantBeMoved` passive, which is printed and
   * permanent.
   */
  | { k: 'cantMove'; player: PlayerId; object: ObjectId }
  /**
   * 705. "Prevent all spell and ability damage this turn." Combat damage is a
   * separate source and is not covered, which is why the kind names what it
   * stops rather than being a blanket shield.
   */
  | { k: 'preventEffectDamage'; player: PlayerId }
  /**
   * 704/363.3. "The next time it dies this turn, recall it exhausted instead."
   *
   * A replacement armed for one occurrence rather than a standing passive: it
   * names the object it is watching, carries the source whose instructions run,
   * and is discarded once it fires.
   */
  | {
    k: 'nextDeath'; player: PlayerId; object: ObjectId;
    source: ObjectId; effects: Instruction[];
  }
  /** 705. The same, for the next damage that would be dealt to one object. */
  | {
    k: 'nextDamage'; player: PlayerId; object: ObjectId;
    source: ObjectId; effects: Instruction[];
  }
  /**
   * 469. "When it wins a combat this turn, gain 2 XP." Armed on one unit and
   * spent by the first combat its side wins while it is in it -- a combat has
   * no event of its own that names a *unit*, only the players.
   */
  | {
    k: 'winsCombat'; player: PlayerId; object: ObjectId;
    source: ObjectId; effects: Instruction[];
  }
  /**
   * 363.3. "This turn, that unit has 'When I conquer, you may move me to my
   * base.'" An ability lent to one object for the turn. `grantsAbility` is the
   * standing form; this one expires, so it lives with the turn effects.
   */
  | {
    k: 'grantedAbility'; player: PlayerId; object: ObjectId;
    ability: Ability;
  }
  /** 363.3. "The next time you play a unit this turn, ready it." */
  | {
    k: 'nextUnitPlayed'; player: PlayerId;
    source: ObjectId; effects: Instruction[];
  }
  /**
   * 363.2. "When a friendly unit is played this turn, buff it." Every unit
   * that arrives this turn, not just the next one, and narrowed by a filter.
   */
  | {
    k: 'onUnitPlayed'; player: PlayerId; filter: ObjectFilter;
    source: ObjectId; effects: Instruction[];
  };

export interface RestrictedEnergy {
  energy: number;
  only: SpendOnly;
}

export interface RunePool {
  energy: number;
  power: Record<Domain, number>;
  any: number;
  /**
   * 204. Energy that may only be spent for a particular purpose. Kept apart
   * from `energy` so it can never quietly pay for something it should not, and
   * spent before free Energy when it does apply.
   */
  restricted: RestrictedEnergy[];
}

/**
 * A cost in the abstract. `power` counts pips; which domains can pay them is
 * decided by `powerDomains` -- an empty list means [A], any domain (135.2.e.6.b).
 */
export interface Cost {
  energy: number;
  power: number;
  /** Domains that may pay the Power component. Empty = any. */
  powerDomains: Domain[];
}

// -- keywords ----------------------------------------------------------------

/** 800. Keywords that the engine implements mechanically. */
export type Keyword =
  | 'accelerate' | 'action' | 'assault' | 'deathknell' | 'deflect' | 'ganking'
  | 'hidden' | 'legion' | 'reaction' | 'shield' | 'tank' | 'temporary'
  | 'vision' | 'equip' | 'quickDraw' | 'repeat' | 'weaponmaster' | 'ambush'
  | 'hunt' | 'level' | 'unique' | 'backline' | 'empower' | 'empowered'
  | 'flow';

/** Keywords that carry a numeric value; absent X is 1 (e.g. 807.1.b.3). */
export type KeywordValues = Partial<Record<Keyword, number>>;

// -- static card data --------------------------------------------------------

/** The printed card. Immutable for the life of the game. */
export interface CardDef {
  id: string;
  name: string;
  /** Champion tag linking legends, champion units and signature cards (133.8.b). */
  tag: string | null;
  /** 133.8. Every printed tag, including factions and species. */
  tags: string[];
  type: CardType;
  superType: SuperType | null;
  rarity: Rarity;
  domains: Domain[];
  cost: Cost;
  might: number | null;
  /** 137. Might bonus an attached equipment grants its host. */
  mightBonus: number | null;
  text: string;
  /** 136. Text that is active only while this card is attached to another. */
  effectText: string;
  keywords: Keyword[];
  keywordValues: KeywordValues;
  /** True when the printed text carries rules beyond its keywords. */
  hasUnimplementedText: boolean;
}

// -- game objects ------------------------------------------------------------

/** 124.2. Statuses that live on a board object and clear when it changes zones. */
export type Status =
  | 'attacking' | 'defending' | 'stunned' | 'empowered' | 'buffed' | 'facedown';

/** A temporary modification applied by an effect, with an expiry (317.2.c). */
export interface Modification {
  /**
   * 'shield' is 355.9.b applied for a limited time: "It can't be chosen by
   * enemy spells and abilities this turn." The passive form of this is a
   * standing effect on the card; this is the one an effect hands out.
   */
  kind: 'might' | 'keyword' | 'shield';
  /** For 'might': the delta. For 'keyword': the value (e.g. Assault N). */
  amount: number;
  keyword?: Keyword;
  /** 'turn' expires in the Expiration Step; 'combat' at end of combat. */
  duration: 'turn' | 'combat' | 'permanent';
  source: ObjectId;
}

export interface GameObject {
  id: ObjectId;
  card: string;
  owner: PlayerId;
  controller: PlayerId;
  zone: Zone;
  /** Only set while the object is on the board. */
  location: Location | null;
  exhausted: boolean;
  damage: number;
  statuses: Status[];
  /**
   * 447. Which card banished this one. "Four spells banished with me" is a
   * question about the banisher, so the banished card has to remember it --
   * the banishment zone is shared and otherwise anonymous.
   */
  banishedBy?: ObjectId | null;
  /**
   * 441. How many times this has been Empowered.
   *
   * Empower is binary for almost every card, and the `empowered` status stays
   * the thing everything else reads. A handful print "I can be [Empowered] up
   * to three times" and count the stacks, which a boolean cannot answer.
   */
  empowerCount?: number;
  /**
   * 137.2. How many buffs this is carrying. One is the cap unless something
   * lifts it (`multiBuff`); the `buffed` status stays the thing everything
   * else reads, exactly as with Empower.
   */
  buffCount?: number;
  /**
   * 133.8. Tags this object has beyond the ones its card prints: "As you play
   * me, choose Bird, Cat, Dog, or Poro. I gain that tag."
   */
  tags?: string[];
  mods: Modification[];
  attachedTo: ObjectId | null;
  /** 818. The turn this became attached, for "if this was attached to me this turn". */
  attachedTurn?: number;
  attachments: ObjectId[];
  /**
   * 187.4. Who this object goes back to when a borrowed control ends, and what
   * ends it. Null when nobody has borrowed it.
   *
   * Held on the object rather than as a turn effect because one of the two
   * durations outlives the turn: control lasts until the *taker* leaves the
   * board, which may be several turns later.
   */
  borrowedControl: { from: PlayerId; until: 'turn' | 'whileSourceOnBoard'; by: ObjectId } | null;
  /** Turn number on which this object entered its current zone. */
  enteredTurn: number;
  /** Moves made this turn, for "the third time I move in a turn" style text. */
  movesThisTurn: number;
  /**
   * 705. Whether anything has damaged this object this turn. Not the same as
   * `damage`: healing clears the marks but not the fact that it was hit, and
   * "if I haven't been dealt damage this turn" asks about the fact.
   */
  damagedThisTurn?: boolean;
  /** 471. Whether this object conquered a battlefield this turn. */
  conqueredThisTurn: boolean;
  /**
   * Ability keys used this turn, for "Use only once per turn" (371). Triggered
   * abilities record themselves under their trigger index.
   */
  usedThisTurn: string[];
  /**
   * 133.8. A word this permanent named as it was played: a tag ("The List") or
   * a card name ("Fallen Feline").
   *
   * On the object rather than in `turnEffects` because it lasts as long as the
   * permanent does, and two copies of the same card name independently.
   */
  named?: string;
  /**
   * 185. The battlefield this token was put in place of.
   *
   * Recorded so a token that says "replace this with the battlefield it
   * replaced" has something to name: the card it displaced is in banishment,
   * and nothing else on the state remembers which one it was.
   */
  replaced?: ObjectId;
  /**
   * 144. Where this object was standing before its most recent move.
   *
   * "A unit at a battlefield I moved to or from" is asked *after* the move, by
   * which time the origin is only in the event log -- and a triggered ability
   * resolves off the chain, where the event that raised it is long gone.
   */
  movedFrom?: Location;
  /**
   * 818. A copy that lasts only while an Equipment stays attached: the card
   * this object printed before it was overwritten, and the attachment holding
   * the copy in place.
   *
   * On the object rather than in `turnEffects` because it can outlast the turn
   * -- an Equipment stays on until something takes it off.
   */
  copiedWhileAttached?: { was: string; by: ObjectId };
  /**
   * 706. How many times this object's effects have dealt damage this turn.
   *
   * Counted on the object rather than on the player because "each time *this
   * spell* has dealt damage this turn" is about one card, and a spell that
   * goes to the trash and is played again is still the same object.
   */
  damageInstancesThisTurn?: number;
  /**
   * 349. The zone this card was played from, for "units played from anywhere
   * other than a player's hand". Null for anything never played -- runes
   * channelled straight to the board, tokens created by an effect.
   */
  playedFrom: ZoneKind | null;
}

// -- battlefields ------------------------------------------------------------

export interface BattlefieldState {
  index: number;
  /** Object id of the battlefield card itself. */
  object: ObjectId;
  controller: PlayerId | null;
  /** 190.3: the player who applied Contested, or null if uncontested. */
  contestedBy: PlayerId | null;
  /** Players who have already Scored here this turn (470). */
  scoredThisTurn: PlayerId[];
  /**
   * 471. Players who *conquered* here this turn, which is the narrower of the
   * two ways to score and the one "a battlefield you conquered this turn"
   * names. Cleared with `scoredThisTurn`.
   */
  conqueredThisTurn: PlayerId[];
  showdownStaged: boolean;
  combatStaged: boolean;
}

// -- the chain ---------------------------------------------------------------

export type ChainItemKind = 'spell' | 'permanent' | 'ability';

export interface ChainItem {
  id: number;
  kind: ChainItemKind;
  /** The card or permanent being played, or the source of the ability. */
  source: ObjectId;
  /** Card id of the source, kept so a resolving item survives its source moving. */
  sourceCard: string;
  controller: PlayerId;
  /** 329: Pending until the Check Legality step, then Finalized. */
  finalized: boolean;
  /** Targets chosen during Make Relevant Choices (355). */
  targets: ObjectId[];
  /** For units: the location chosen at 355.2. */
  destination: Location | null;
  /** Optional additional costs the controller elected to pay (356.2.b). */
  paidAccelerate: boolean;
  /** 356.2. Whether the card's additional cost was paid as it was played. */
  paidAdditional: boolean;
  /**
   * 829.1.b. Played from the trash for its Flow cost, which banishes it
   * instead of trashing it when it finishes resolving.
   */
  paidFlow?: boolean;
  /** Instructions for an ability item; spells read theirs from the card script. */
  effects?: Instruction[];
  /** 346.1: focus does not pass when the chain was opened by a trigger. */
  fromTrigger?: boolean;
  /** 429.2: resource abilities resolve as soon as they finalize. */
  addsResources?: boolean;
  /** 203.1. What the ability's cost was paid with, for "the unit you exhausted". */
  paidWith?: ObjectId[];
  /** 820. The Repeat cost was paid, so this spell's effects run twice. */
  paidRepeat?: boolean;
  /** Identifies a built-in ability when kind === 'ability'. */
  ability?: string;
}

// -- turn structure ----------------------------------------------------------

/** 314. */
export type Phase = 'awaken' | 'beginning' | 'channel' | 'draw' | 'main' | 'ending';

/** 307-310. The two independent axes that gate what may be played. */
export type ShowdownState = 'neutral' | 'showdown';
export type OpenState = 'open' | 'closed';

/** 463. Where an in-progress combat currently is. */
export type CombatStep = 'showdown' | 'damage' | 'resolution';

export interface CombatState {
  battlefield: number;
  attacker: PlayerId;
  defender: PlayerId;
  step: CombatStep;
  /** False for a Non-Combat Showdown (316.8.b.1.a). */
  isCombat: boolean;
}

// -- players -----------------------------------------------------------------

export interface PlayerState {
  id: PlayerId;
  name: string;
  points: number;
  pool: RunePool;
  /** 728. Spent by Level and various abilities. */
  xp: number;
  /** Card ids, most recent last, for "you have played another card this turn" (812). */
  cardsPlayedThisTurn: number;
  /**
   * 355.9. How many enemy objects this player has chosen this turn with spells
   * and abilities. "Use only if you've chosen an enemy unit this turn" is a
   * fact about the act of choosing, which no board state records.
   */
  chosenEnemyThisTurn: number;
  /**
   * 376. Card ids of the permanents whose activated abilities this player has
   * used this turn. "The first friendly gear activated ability played each
   * turn costs [1] less" needs to know whether one has gone already, and no
   * board state records it -- `usedThisTurn` is per object and per ability.
   */
  abilitiesUsedThisTurn: string[];
  /**
   * Resources actually spent this turn, for "if you've spent [4] or more this
   * turn". Counted where the pool is debited, so it cannot drift from it.
   */
  spentThisTurn: { energy: number; power: number };
  /**
   * 705.4. Damage assigned to units beyond what was lethal, this turn. Read by
   * "if you assigned 5 or more excess damage".
   */
  excessDamageThisTurn: number;
  /**
   * Units this player controlled that died this turn, for "if a friendly unit
   * died this turn". Counted where the death happens rather than scanned from
   * the trash, which cannot tell this turn's dead from any other turn's.
   */
  unitsDiedThisTurn: number;
  /**
   * 315. How many of this player's units died during their own Beginning
   * Phase this turn. Kept apart from `unitsDiedThisTurn` because Temporary
   * kills happen there and a card can ask about exactly that window.
   */
  unitsDiedInBeginningPhase: number;
  /** "if you've gained XP this turn" -- reset with the rest at end of turn. */
  xpGainedThisTurn: number;
  /**
   * 471. Points scored by *holding* this turn, which some cards count
   * separately from points scored by conquering.
   */
  pointsHeldThisTurn: number;
  /**
   * Cards this player has played this turn, by card id.
   *
   * Kept as ids rather than as a tally per type, because the questions asked
   * are about what the cards *were* -- "a non-token unit, a non-token gear and
   * a non-token spell" -- which only the card index can answer.
   */
  playedThisTurn: string[];
  /** Set once the player has taken their first turn, for the mode's first-turn rules. */
  turnsTaken: number;
  hasConceded: boolean;
}

// -- events ------------------------------------------------------------------

/**
 * Everything the engine does is announced here. The renderer animates from this
 * log, and the tests assert against it, so an action that changes the game and
 * emits nothing is a bug.
 */
export type GameEvent =
  | { t: 'phase'; phase: Phase; player: PlayerId; turn: number }
  | { t: 'draw'; player: PlayerId; object: ObjectId; card: string }
  | { t: 'burnOut'; player: PlayerId; gavePointTo: PlayerId }
  | { t: 'channel'; player: PlayerId; object: ObjectId; card: string; exhausted: boolean }
  | { t: 'mulligan'; player: PlayerId; count: number }
  | { t: 'play'; player: PlayerId; object: ObjectId; card: string; to: Location | null }
  | { t: 'resolve'; item: number; object: ObjectId; card: string }
  | { t: 'counter'; item: number }
  | { t: 'move'; object: ObjectId; from: Location; to: Location }
  | { t: 'recall'; object: ObjectId; from: Location; to: Location }
  | { t: 'hide'; player: PlayerId; object: ObjectId; battlefield: number }
  /**
   * 811. "You can look at their facedown cards this turn." The engine hides
   * nothing from itself, so this is the whole of the effect: an announcement
   * a UI can act on.
   */
  | { t: 'lookFacedown'; player: PlayerId; at: PlayerId }
  | { t: 'exhaust'; object: ObjectId }
  | { t: 'ready'; object: ObjectId }
  | { t: 'damage'; object: ObjectId; amount: number; source: ObjectId | null }
  | { t: 'heal'; object: ObjectId }
  | { t: 'kill'; object: ObjectId; card: string; by: ObjectId | null }
  | { t: 'discard'; player: PlayerId; object: ObjectId; card: string }
  | { t: 'recycle'; player: PlayerId; objects: ObjectId[] }
  | { t: 'addResources'; player: PlayerId; energy: number; power: number; domain: Domain | null }
  | { t: 'poolEmptied'; player: PlayerId }
  | { t: 'contest'; battlefield: number; by: PlayerId }
  | { t: 'controlChanged'; battlefield: number; from: PlayerId | null; to: PlayerId | null }
  | { t: 'score'; player: PlayerId; battlefield: number; method: 'hold' | 'conquer'; points: number }
  | { t: 'pointsGained'; player: PlayerId; amount: number; total: number }
  | { t: 'finalPointDenied'; player: PlayerId; battlefield: number }
  | { t: 'showdownBegin'; battlefield: number; focus: PlayerId }
  | { t: 'showdownEnd'; battlefield: number }
  | { t: 'combatBegin'; battlefield: number; attacker: PlayerId; defender: PlayerId }
  | { t: 'combatDamage'; battlefield: number; assignments: { object: ObjectId; amount: number }[] }
  | { t: 'combatResult'; battlefield: number; winner: PlayerId | null }
  | { t: 'combatEnd'; battlefield: number }
  | { t: 'priority'; player: PlayerId | null }
  | { t: 'focus'; player: PlayerId | null }
  | { t: 'turnEnd'; player: PlayerId }
  | { t: 'gameOver'; winner: PlayerId | null; reason: string };

// -- actions -----------------------------------------------------------------

/**
 * 410.1. The discretionary choices a player can make. `legalActions` enumerates
 * exactly these, and nothing outside the returned list is accepted.
 */
export type Action =
  | { t: 'mulligan'; cards: ObjectId[] }
  /** 349. Play a card from hand or the Champion Zone. */
  | {
    t: 'play'; object: ObjectId; to?: Location; targets?: ObjectId[];
    /** 829. Played from the trash for its Flow cost. */
    flow?: boolean;
    /** 356.4. Played for the alternative price the card offers. */
    alt?: boolean;
    /**
     * 356.4. Played from the trash for a price a resolution granted, rather
     * than for one printed on the card.
     *
     * Distinct from `flow` because the permission is: Flow is on the card and
     * good every turn, while this is a `playFromTrash` turn effect that a
     * resolution left behind, and it is spent when taken.
     */
    fromTrashOffer?: boolean;
    /**
     * 356.2. The permanent whose offered additional cost this play is taking:
     * "any player may pay [A][A] ... they play it to this battlefield".
     *
     * Named on the action because the price and the destination both come from
     * it, and the payment has to read the same offer the enumeration priced.
     */
    grantedBy?: ObjectId;
    /** 820. Paid the Repeat cost, so the spell's effects happen twice. */
    repeat?: boolean;
    accelerate?: boolean;
    /** 356.2. Whether the player is taking the card's optional extra cost. */
    additional?: boolean;
  }
  /** 144. Exhaust one or more units to move them to a shared destination. */
  | { t: 'move'; objects: ObjectId[]; to: Location }
  /** 376. Activate an ability by its key on a board object. */
  | { t: 'activate'; object: ObjectId; ability: string; targets?: ObjectId[] }
  /** 811. Put a card with Hidden facedown at a battlefield you control. */
  | { t: 'hide'; object: ObjectId; battlefield: number }
  /** 465.2.c. Order in which this player's damage is assigned to enemy units. */
  | { t: 'assignDamage'; order: ObjectId[] }
  /**
   * 355.3 / 424. Answers the question an effect stopped to ask.
   *
   * `pick` is an object id when the choice is between cards and an index into
   * the printed list when it is between modes; `state.pendingChoice` says
   * which, and enumerates exactly what may be picked.
   */
  | { t: 'choose'; pick: number }
  /** Pass priority (338.1.b) or pass focus in a Showdown (347.2). */
  | { t: 'pass' }
  /** 316.9. End the Main Phase. */
  | { t: 'endTurn' }
  | { t: 'concede' };

// -- game state --------------------------------------------------------------

export interface GameConfig {
  /** 483.3. 8 for every sanctioned mode. */
  victoryScore: number;
  /** 485.4. One battlefield contributed by each player in a Duel. */
  battlefieldCount: number;
  /** 485.7. The player going second channels an extra rune on their first turn. */
  extraRuneForSecondPlayer: boolean;
  startingHand: number;
  mulliganLimit: number;
  runesPerChannel: number;
  /** Safety valve: abort rather than spin if the state machine fails to settle. */
  maxCleanupIterations: number;
}

export const DUEL: GameConfig = {
  victoryScore: 8,
  battlefieldCount: 2,
  extraRuneForSecondPlayer: true,
  startingHand: 4,
  mulliganLimit: 2,
  runesPerChannel: 2,
  maxCleanupIterations: 64,
};

/**
 * 355.3 / 424. A choice an effect stopped in the middle of itself to ask.
 *
 * The engine cannot suspend a call stack, so it does not: it discards the
 * half-finished resolution, records the question here, and replays the whole
 * resolution once the answer arrives, reading the log instead of asking. That
 * is only sound because resolution is deterministic -- the seed is restored
 * with everything else, so the replay reveals the same cards in the same
 * order and reaches the same question.
 */
/**
 * The answer that ends an optional choice.
 *
 * Negative so it can never collide with an object id, which are positive and
 * handed out in order.
 */
export const DECLINE = -1;

export interface PendingChoice {
  player: PlayerId;
  /**
   * Which suspension is waiting on this answer, so `applyAction` files it in
   * the right log. Absent means a resolution, which is the common case.
   */
  for?: 'trigger';
  /** A short line naming what is being chosen, for the client to show. */
  prompt: string;
  /** The cards on offer, when the choice is between cards. */
  cards?: ObjectId[];
  /**
   * 436.1.a. The words on a button that ends an optional choice.
   *
   * "Look at the top two cards ... you may recycle one or both of them" is one
   * decision taken with both cards in view, not a run of blind yes/no
   * questions: the player looks at everything, then recycles any number. So
   * the cards are offered together and this is how the player says "no more".
   */
  decline?: string;
  /**
   * 133.8. The words on offer, when the choice is naming something -- a tag
   * or a card name. `pick` is an index into this list.
   */
  names?: string[];
  /**
   * The modes still on offer (355.3), as indices into the card's printed list.
   *
   * Indices rather than a count because "choose two different modes" asks
   * twice and must not offer the same one again.
   */
  modes?: number[];
  /**
   * The printed words for each mode, indexed by mode number.
   *
   * Sparse where the compiler inferred the modes rather than reading them off
   * a printed list, so a client numbers whatever is missing.
   */
  modeLabels?: string[];
  /** The source of the effect asking, so the client can name it. */
  source: ObjectId;
}

/** What the engine is currently waiting on. */
export type Waiting =
  | { kind: 'none' }
  /** 355.3 / 424. An effect has stopped to ask its controller something. */
  | { kind: 'choose'; player: PlayerId }
  | { kind: 'mulligan'; player: PlayerId }
  | { kind: 'priority'; player: PlayerId }
  | { kind: 'assignDamage'; player: PlayerId }
  | { kind: 'gameOver' };

export interface GameState {
  config: GameConfig;
  /** splitmix32 state; advancing it is the only source of randomness. */
  seed: number;
  turn: number;
  /**
   * Continuous effects that last only for the current turn ("Opponents can't
   * play cards this turn"). Cleared in the end step, so nothing has to
   * remember to expire itself.
   */
  turnEffects: TurnEffect[];
  /**
   * 314. Turns queued by "Take a turn after this one", oldest first.
   *
   * Held in state rather than taken immediately: the turn that granted it has
   * to finish first, and the cleanup loop decides when that is.
   */
  extraTurns: PlayerId[];
  /**
   * Things that have happened and not yet been offered to triggered abilities.
   *
   * `ops` performs the game action but must not reach into the trigger system
   * (the engine depends on ops, not the other way round), so it records what
   * happened here and the engine drains the queue on its next pass.
   */
  pendingTriggers: TriggerEvent[];
  /**
   * 363.3. Effects scheduled for a later moment: "Ready 2 runes at the end of
   * this turn", "[Add] [A] at the start of your next Main Phase".
   *
   * Held on the state rather than on the card that created them, because the
   * card may be gone by the time they fire.
   */
  delayed: DelayedEffect[];
  turnPlayer: PlayerId;
  phase: Phase;
  players: [PlayerState, PlayerState];
  objects: Record<ObjectId, GameObject>;
  nextObjectId: ObjectId;

  /** Ordered card lists per zone. Board membership is derived from objects. */
  zones: {
    mainDeck: [ObjectId[], ObjectId[]];
    runeDeck: [ObjectId[], ObjectId[]];
    hand: [ObjectId[], ObjectId[]];
    trash: [ObjectId[], ObjectId[]];
    banishment: [ObjectId[], ObjectId[]];
    championZone: [ObjectId[], ObjectId[]];
    legendZone: [ObjectId[], ObjectId[]];
  };

  battlefields: BattlefieldState[];

  chain: ChainItem[];
  nextChainId: number;

  showdownState: ShowdownState;
  openState: OpenState;
  combat: CombatState | null;

  priority: PlayerId | null;
  focus: PlayerId | null;
  /** Consecutive passes with no chain additions (339.1). */
  passes: number;

  /**
   * 355.3 / 424. The question the current resolution stopped on, if any.
   *
   * Set only between the abandoned attempt and the answer; a state in this
   * condition has already been rolled back to just before the resolution
   * started, so nothing half-done is visible.
   */
  pendingChoice: PendingChoice | null;
  /**
   * Answers given to the resolution in progress, oldest first.
   *
   * Consumed in order on each replay and cleared when the resolution finally
   * completes. Part of the state rather than a parameter because a replay has
   * to see the same answers a clone of this state would.
   */
  choiceLog: number[];
  /**
   * How much of `choiceLog` the resolution in progress has consumed.
   *
   * Reset at the top of every attempt rather than carried, so a replay reads
   * the same answers in the same order as the pass that recorded them.
   */
  choiceCursor: number;
  /**
   * 359.3. The same pair, for a trigger choosing its targets as it goes on the
   * chain.
   *
   * A separate log rather than a second user of `choiceLog`, because the two
   * suspensions can be live at the same moment: a resolution that stopped to
   * ask is rolled back, and the cleanup that runs before the chain is stepped
   * again can queue a fresh trigger. Sharing one log there would have the
   * trigger read the resolution's answers as its own.
   */
  triggerChoiceLog: number[];
  triggerChoiceCursor: number;

  /** Combat damage still to be assigned, keyed by the assigning player. */
  pendingAssignment: { player: PlayerId; total: number; targets: ObjectId[] } | null;
  /** Assignments already made this damage step, awaiting the other player. */
  assignments: { object: ObjectId; amount: number }[];

  waiting: Waiting;
  winner: PlayerId | null;
  events: GameEvent[];
  /** Incremented every time the state is mutated; lets views skip redundant work. */
  version: number;
}
